import { useEffect, useRef } from "react";

type Point = { x: number; y: number; oldX: number; oldY: number };

const POINTS = 24;
const clamp = (value: number, minimum: number, maximum: number) => Math.min(maximum, Math.max(minimum, value));

export const PhysicsThread = ({ variant = "type" }: { variant?: "type" | "color" }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // jsdom exposes canvas without a rendering context. The thread is visual
    // enhancement only, so tests and non-canvas browsers should simply skip it.
    if (/jsdom/i.test(navigator.userAgent)) return;
    const context = canvas.getContext("2d");
    if (!context) return;

    const reducedQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const coarseQuery = window.matchMedia("(hover: none), (pointer: coarse)");
    let reduced = reducedQuery.matches || coarseQuery.matches;
    let width = 0;
    let height = 0;
    let ratio = 1;
    let frame = 0;
    let lastTime = performance.now();
    let points: Point[] = [];
    let target = { x: 0, y: 0 };
    let resting = { x: 0, y: 0 };
    let engaged = false;

    const anchor = () => variant === "type"
      ? { x: width * 0.13, y: height * 0.19 }
      : { x: width * 0.14, y: height * 0.72 };

    const makePoints = () => {
      const start = anchor();
      resting = variant === "type"
        ? { x: width * 0.84, y: height * 0.67 }
        : { x: width * 0.82, y: height * 0.28 };
      target = { ...resting };
      points = Array.from({ length: POINTS }, (_, index) => {
        const amount = index / (POINTS - 1);
        const x = start.x + (resting.x - start.x) * amount;
        const y = start.y + (resting.y - start.y) * amount + Math.sin(amount * Math.PI) * height * 0.045;
        return { x, y, oldX: x, oldY: y };
      });
    };

    const resize = () => {
      const bounds = canvas.getBoundingClientRect();
      width = Math.max(1, bounds.width);
      height = Math.max(1, bounds.height);
      ratio = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      makePoints();
    };

    const pointerMove = (event: PointerEvent) => {
      if (reduced) return;
      const bounds = canvas.getBoundingClientRect();
      const inside = event.clientX >= bounds.left && event.clientX <= bounds.right && event.clientY >= bounds.top && event.clientY <= bounds.bottom;
      engaged = inside;
      if (!inside) return;
      target = {
        x: clamp(event.clientX - bounds.left, width * 0.12, width * 0.94),
        y: clamp(event.clientY - bounds.top, height * 0.08, height * 0.92),
      };
    };

    const pointerLeave = () => {
      engaged = false;
      target = { ...resting };
    };

    const draw = () => {
      context.clearRect(0, 0, width, height);
      if (points.length < 2) return;
      const gradient = context.createLinearGradient(0, 0, width, height);
      if (variant === "type") {
        gradient.addColorStop(0, "rgba(235, 66, 143, 0.96)");
        gradient.addColorStop(0.64, "rgba(235, 66, 143, 0.88)");
        gradient.addColorStop(1, "rgba(62, 87, 224, 0.92)");
      } else {
        gradient.addColorStop(0, "rgba(255, 189, 91, 0.92)");
        gradient.addColorStop(0.46, "rgba(238, 72, 146, 0.96)");
        gradient.addColorStop(1, "rgba(95, 127, 244, 0.96)");
      }
      context.beginPath();
      context.moveTo(points[0]!.x, points[0]!.y);
      for (let index = 1; index < points.length - 1; index += 1) {
        const point = points[index]!;
        const next = points[index + 1]!;
        context.quadraticCurveTo(point.x, point.y, (point.x + next.x) / 2, (point.y + next.y) / 2);
      }
      const end = points.at(-1)!;
      context.lineTo(end.x, end.y);
      context.strokeStyle = gradient;
      context.lineWidth = engaged ? 2.4 : 1.65;
      context.lineCap = "round";
      context.lineJoin = "round";
      context.stroke();

      context.beginPath();
      context.arc(end.x, end.y, engaged ? 5.5 : 3.8, 0, Math.PI * 2);
      context.fillStyle = variant === "type" ? "#f5efe5" : "#fff4df";
      context.fill();
      context.lineWidth = 1.5;
      context.strokeStyle = variant === "type" ? "#e3428b" : "#ef4b93";
      context.stroke();
    };

    const tick = (time: number) => {
      const delta = Math.min(1.8, Math.max(0.45, (time - lastTime) / 16.667));
      lastTime = time;
      if (!engaged) target = { ...resting };

      const start = anchor();
      const end = points.at(-1);
      if (end && !reduced) {
        end.x += (target.x - end.x) * 0.12 * delta;
        end.y += (target.y - end.y) * 0.12 * delta;
      }

      if (!reduced) {
        for (let index = 1; index < points.length - 1; index += 1) {
          const point = points[index]!;
          const velocityX = (point.x - point.oldX) * 0.925;
          const velocityY = (point.y - point.oldY) * 0.925;
          point.oldX = point.x;
          point.oldY = point.y;
          point.x += velocityX * delta;
          point.y += velocityY * delta + 0.12 * delta;
        }
        points[0] = { x: start.x, y: start.y, oldX: start.x, oldY: start.y };

        const last = points.at(-1)!;
        const distance = Math.hypot(last.x - start.x, last.y - start.y);
        const segment = Math.max(4, distance / (POINTS - 1) * 1.035);
        for (let pass = 0; pass < 7; pass += 1) {
          points[0]!.x = start.x;
          points[0]!.y = start.y;
          for (let index = 0; index < points.length - 1; index += 1) {
            const left = points[index]!;
            const right = points[index + 1]!;
            const dx = right.x - left.x;
            const dy = right.y - left.y;
            const current = Math.max(0.001, Math.hypot(dx, dy));
            const correction = (current - segment) / current;
            const share = index === 0 ? 1 : 0.5;
            if (index > 0) {
              left.x += dx * correction * 0.5;
              left.y += dy * correction * 0.5;
            }
            right.x -= dx * correction * share;
            right.y -= dy * correction * share;
          }
          last.x += (target.x - last.x) * (engaged ? 0.18 : 0.1);
          last.y += (target.y - last.y) * (engaged ? 0.18 : 0.1);
        }
      }

      draw();
      frame = requestAnimationFrame(tick);
    };

    const motionChanged = () => {
      reduced = reducedQuery.matches || coarseQuery.matches;
      engaged = false;
      makePoints();
      draw();
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    window.addEventListener("pointermove", pointerMove, { passive: true });
    window.addEventListener("pointerleave", pointerLeave);
    window.addEventListener("blur", pointerLeave);
    reducedQuery.addEventListener("change", motionChanged);
    coarseQuery.addEventListener("change", motionChanged);
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("pointermove", pointerMove);
      window.removeEventListener("pointerleave", pointerLeave);
      window.removeEventListener("blur", pointerLeave);
      reducedQuery.removeEventListener("change", motionChanged);
      coarseQuery.removeEventListener("change", motionChanged);
    };
  }, [variant]);

  return <canvas ref={canvasRef} className="physics-thread" aria-hidden="true" />;
};
